import type { EngineOilTypeData, EngineOilTypeEntry } from '@avisoma/shared';
import { PageIdsCell, presentPmaDerScalarEntries } from './PmaDerTables';
import { Td, Th, TruncatedTd } from './cells';
import { extractionRowClass, extractionTableClass, extractionTbodyClass, extractionTheadClass } from './tableClasses';

type OilRow = EngineOilTypeEntry & { pageIds?: string[] };

/**
 * Engine oil type statement (DR-6375): the letter scalars a PMA/DER statement carries, plus the
 * aircraft the letter names. Rows carry `pageIds` (plural) after consolidation.
 */
export function presentOilTypeScalarEntries(
  statement: Omit<EngineOilTypeData, 'oils'> | null | undefined,
): ReadonlyArray<readonly [string, string]> {
  if (!statement) return [];
  const entries = [...presentPmaDerScalarEntries(statement)];
  if (statement.aircraftRegistration) entries.push(['Aircraft Registration', statement.aircraftRegistration]);
  if (statement.aircraftMsn) entries.push(['Aircraft MSN', statement.aircraftMsn]);
  return entries;
}

export function OilTypeOilsTable({
  rows,
  pageIdToPosition,
  onInspect,
}: {
  rows: OilRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: OilRow) => void;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {pageIdToPosition && <Th>Pages</Th>}
          <Th>Specification</Th>
          <Th>Product</Th>
          <Th>Current</Th>
          <Th>Alternate</Th>
          <Th>Quote</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((row, index) => {
          const pageIds = row.pageIds ?? [];
          return (
            <tr
              key={`${row.specification ?? ''}-${row.product ?? ''}-${pageIds[0] ?? 'page'}-${index}`}
              onClick={() => onInspect(row)}
              className={extractionRowClass}
            >
              {pageIdToPosition && (
                <Td>
                  <PageIdsCell pageIds={pageIds} pageIdToPosition={pageIdToPosition} />
                </Td>
              )}
              <Td>{row.specification ?? '—'}</Td>
              <Td>{row.product ?? '—'}</Td>
              <Td>{row.current ? 'Yes' : '—'}</Td>
              <Td>{row.alternate ? 'Yes' : '—'}</Td>
              <TruncatedTd value={row.quote} maxWidth="max-w-[480px]" />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
